import moment from 'moment';
import * as React from 'react';
import { useContext, useState } from 'react';
import {
  Button,
  Col,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  UncontrolledDropdown,
} from 'reactstrap';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useObjectVal } from 'react-firebase-hooks/database';
import { getAuth } from 'firebase/auth';
import { AbilityContext, Can } from '../../auth/Can';
import { PlusIcon } from '../../components/Icons';
import { ColumnsSelector } from '../../components/table/ColumnsSelector';
import { ExportToExcel } from '../accounts/ExportToExcel';
import { getDAMApp } from '../../services/firebase';
import { ref } from '../../utils/firebase';
import { Workflows, WorkflowsToolbarApi } from './Workflows';
import { WorkflowModal } from './WorkflowModal';
import {
  getWorkflowDisplayStatus,
  WorkflowDisplayStatus,
  WorkflowSearchScope,
} from './utils';
import * as matchSorter from 'match-sorter';
import classnames from 'classnames';

const pagesStyles = require('../pages.css');
const workflowsStyles = require('./workflows.css');

const fuzzyTextFilterFn = (rows, id, filterValue) => {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
};

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;
export const fuzzyTextFilterFnExp = fuzzyTextFilterFn;

// Tab indices follow traffic-light order (green -> amber -> red), then Archived.
// IMPORTANT: there is deliberately no "All Workflows" tab — it was removed by
// product spec and must not be reinstated; Active is the default landing tab.
const TAB_ACTIVE = 0;
const TAB_PAUSED = 1;
const TAB_ERRORED = 2;
const TAB_ARCHIVED = 3;

const EXPIRING_SOON_DAYS = 7;

type Metrics = Record<WorkflowDisplayStatus, number> & {
  total: number;
  expiringSoon: number;
};

const computeMetrics = (
  workflowsList: Record<string, any> | null | undefined
): Metrics => {
  const metrics: Metrics = {
    [WorkflowDisplayStatus.ACTIVE]: 0,
    [WorkflowDisplayStatus.PAUSED]: 0,
    [WorkflowDisplayStatus.ERRORED]: 0,
    [WorkflowDisplayStatus.ARCHIVED]: 0,
    total: 0,
    expiringSoon: 0,
  };

  if (!workflowsList) {
    return metrics;
  }

  const now = moment().unix();
  const expiryCutoff = moment().add(EXPIRING_SOON_DAYS, 'days').unix();

  Object.keys(workflowsList).forEach((key) => {
    const workflow = workflowsList[key];
    metrics[getWorkflowDisplayStatus(workflow)] += 1;
    metrics.total += 1;

    // Legacy endDate field: still surfaced for workflows that carry one
    const endDate = parseInt(workflow.endDate, 10);
    if (
      workflow.isArchived !== true &&
      !isNaN(endDate) &&
      endDate >= now &&
      endDate <= expiryCutoff
    ) {
      metrics.expiringSoon += 1;
    }
  });

  return metrics;
};

const percentOfTotal = (value: number, total: number) =>
  total > 0 ? `${Math.round((value / total) * 100)}% of total` : '0% of total';

const METRIC_ICON_BOX_PX = 64;
const METRIC_ICON_PX = 40;

const metricIconSvgProps = {
  width: METRIC_ICON_PX,
  height: METRIC_ICON_PX,
  className: workflowsStyles.metricIconSvg,
  'aria-hidden': true as const,
};

const CheckCircleIcon = () => (
  <svg {...metricIconSvgProps} viewBox="0 0 16 16" fill="#28a745">
    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm3.78-9.72a.75.75 0 0 0-1.06-1.06L7.25 8.69 5.28 6.72a.75.75 0 0 0-1.06 1.06l2.5 2.5a.75.75 0 0 0 1.06 0l4-4z" />
  </svg>
);

const PauseCircleIcon = () => (
  <svg {...metricIconSvgProps} viewBox="0 0 16 16" fill="#d39e00">
    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zM6.25 5C6.664 5 7 5.336 7 5.75v4.5a.75.75 0 0 1-1.5 0v-4.5c0-.414.336-.75.75-.75zm3.5 0c.414 0 .75.336.75.75v4.5a.75.75 0 0 1-1.5 0v-4.5c0-.414.336-.75.75-.75z" />
  </svg>
);

const ErrorCircleIcon = () => (
  <svg {...metricIconSvgProps} viewBox="0 0 16 16" fill="#dc3545">
    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zM8 4a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 8 4zm0 7a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
  </svg>
);

const LayersIcon = () => (
  <svg {...metricIconSvgProps} viewBox="0 0 16 16" fill="#0d6efd">
    <path d="M8 .5l8 4-8 4-8-4 8-4zM2.06 7.03L8 10l5.94-2.97L16 8l-8 4-8-4 2.06-.97zm0 3.5L8 13.5l5.94-2.97L16 11.5l-8 4-8-4 2.06-.97z" />
  </svg>
);

const CalendarIcon = () => (
  <svg {...metricIconSvgProps} viewBox="0 0 16 16" fill="#6f42c1">
    <path d="M4.75 0a.75.75 0 0 1 .75.75V2h5V.75a.75.75 0 0 1 1.5 0V2h1.25c.966 0 1.75.784 1.75 1.75v10.5A1.75 1.75 0 0 1 13.25 16H2.75A1.75 1.75 0 0 1 1 14.25V3.75C1 2.784 1.784 2 2.75 2H4V.75A.75.75 0 0 1 4.75 0zM2.5 6v8.25c0 .138.112.25.25.25h10.5a.25.25 0 0 0 .25-.25V6h-11z" />
  </svg>
);

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
  </svg>
);

const MetricCard = ({
  icon,
  iconClass,
  label,
  caption,
  value,
  onClick,
  isActive = false,
}: {
  icon: React.ReactNode;
  iconClass: string;
  label: string;
  caption: string;
  value: number;
  onClick?: () => void;
  isActive?: boolean;
}) => (
  <div
    className={`${workflowsStyles.metricCard} ${
      isActive ? workflowsStyles.metricCardActive : ''
    }`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
  >
    <div
      className={`${workflowsStyles.metricIcon} ${iconClass}`}
      style={{
        width: METRIC_ICON_BOX_PX,
        height: METRIC_ICON_BOX_PX,
        minWidth: METRIC_ICON_BOX_PX,
        minHeight: METRIC_ICON_BOX_PX,
      }}
    >
      {icon}
    </div>
    <div>
      <div className={workflowsStyles.metricLabel}>{label}</div>
      <div className={workflowsStyles.metricValue}>{value}</div>
      <div className={workflowsStyles.metricCaption}>{caption}</div>
    </div>
  </div>
);

export const WorkflowsContainer = () => {
  const damApp = getDAMApp();
  const damAuth = getAuth(damApp);
  const [activeTab, setActiveTab] = useState(TAB_ACTIVE);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [searchScope, setSearchScope] = useState<WorkflowSearchScope>('tab');
  const [toolbarApi, setToolbarApi] = useState<WorkflowsToolbarApi | null>(
    null
  );
  const [user] = useAuthState(damAuth as any);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newWorkflow, setNewWorkflow] = useState(null);

  const ability = useContext(AbilityContext);
  const isWorkflowsAdmin = ability.can('manage', 'workflows_admin');

  // Single listener over all workflows powers metric cards and tab counts;
  // Firebase dedupes this against the per-tab queries on the same ref.
  const [workflowsList] = useObjectVal(ref('workflows') as any);
  const metrics = React.useMemo(
    () => computeMetrics(workflowsList as Record<string, any>),
    [workflowsList]
  );

  const tabs = [
    {
      index: TAB_ACTIVE,
      label: 'Active',
      count: metrics[WorkflowDisplayStatus.ACTIVE],
      dotClass: workflowsStyles.tabDotGreen,
    },
    {
      index: TAB_PAUSED,
      label: 'Paused',
      count: metrics[WorkflowDisplayStatus.PAUSED],
      dotClass: workflowsStyles.tabDotAmber,
    },
    {
      index: TAB_ERRORED,
      label: 'Errored',
      count: metrics[WorkflowDisplayStatus.ERRORED],
      dotClass: workflowsStyles.tabDotRed,
    },
    {
      index: TAB_ARCHIVED,
      label: 'Archived',
      count: metrics[WorkflowDisplayStatus.ARCHIVED],
      dotClass: workflowsStyles.tabDotGrey,
    },
  ];

  const goToActiveTab = () => {
    setActiveTab(TAB_ACTIVE);
  };

  const goToPausedTab = () => {
    setActiveTab(TAB_PAUSED);
  };

  const goToErroredTab = () => {
    setActiveTab(TAB_ERRORED);
  };

  const isActiveTabSelected = activeTab === TAB_ACTIVE;
  const isAllStatusesSearch =
    searchScope === 'all' && Boolean(globalSearchQuery.trim());
  const searchPlaceholder =
    searchScope === 'all'
      ? 'Search all statuses...'
      : 'Search this tab...';

  return (
    <div className={`${pagesStyles.container} ${pagesStyles.fullHeight}`}>
      <div className={workflowsStyles.pageHeader}>
        <div className={workflowsStyles.pageHeaderMain}>
          <h3 className={workflowsStyles.pageTitle}>Workflows</h3>
          <p className={workflowsStyles.pageSubtitle}>
            Manage workflow runs, schedules, migration status, errors and
            lifecycle state.
          </p>
        </div>
      </div>

      {/* Metric cards match tab counts: green (Active, including retrying), amber, red */}
      <div className={workflowsStyles.metricsRow}>
        <MetricCard
          icon={<CheckCircleIcon />}
          iconClass={workflowsStyles.metricIconGreen}
          label="Active"
          caption={percentOfTotal(
            metrics[WorkflowDisplayStatus.ACTIVE],
            metrics.total
          )}
          value={metrics[WorkflowDisplayStatus.ACTIVE]}
          isActive={isActiveTabSelected}
          onClick={goToActiveTab}
        />
        <MetricCard
          icon={<PauseCircleIcon />}
          iconClass={workflowsStyles.metricIconAmber}
          label="Paused"
          caption={percentOfTotal(
            metrics[WorkflowDisplayStatus.PAUSED],
            metrics.total
          )}
          value={metrics[WorkflowDisplayStatus.PAUSED]}
          isActive={activeTab === TAB_PAUSED}
          onClick={goToPausedTab}
        />
        <MetricCard
          icon={<ErrorCircleIcon />}
          iconClass={workflowsStyles.metricIconRed}
          label="Errored"
          caption={percentOfTotal(
            metrics[WorkflowDisplayStatus.ERRORED],
            metrics.total
          )}
          value={metrics[WorkflowDisplayStatus.ERRORED]}
          isActive={activeTab === TAB_ERRORED}
          onClick={goToErroredTab}
        />
        <MetricCard
          icon={<LayersIcon />}
          iconClass={workflowsStyles.metricIconBlue}
          label="Total workflows"
          caption="All time"
          value={metrics.total}
        />
        <MetricCard
          icon={<CalendarIcon />}
          iconClass={workflowsStyles.metricIconPurple}
          label="Expiring soon"
          caption={`End date within ${EXPIRING_SOON_DAYS} days`}
          value={metrics.expiringSoon}
        />
      </div>

      <div className={workflowsStyles.tabsBar}>
        <Nav tabs className={workflowsStyles.tabsRow}>
          {tabs.map((tab) => (
            <NavItem key={tab.index}>
              <NavLink
                className={classnames(workflowsStyles.tabLink, {
                  [workflowsStyles.tabLinkActive]: activeTab === tab.index,
                })}
                onClick={() => setActiveTab(tab.index)}
              >
                <span
                  className={`${workflowsStyles.tabDot} ${tab.dotClass}`}
                />
                {tab.label}
                <span className={workflowsStyles.tabCount}>{tab.count}</span>
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      <div className={workflowsStyles.tableToolbar}>
        <div className={workflowsStyles.tableToolbarActions}>
          <Can I="manage" a="workflows">
            <Button color="primary" onClick={() => setIsCreateModalOpen(true)}>
              <PlusIcon fill="white" /> Create workflow
            </Button>
          </Can>
          {toolbarApi && (
            <>
              <Can I="manage" a="export">
                <ExportToExcel
                  csvData={toolbarApi.exportCsvData}
                  fileName={toolbarApi.exportFilename}
                  user={null}
                  label="Export"
                />
              </Can>
              <UncontrolledDropdown>
                <DropdownToggle
                  outline
                  color="secondary"
                  caret
                  className={workflowsStyles.columnsDropdownToggle}
                >
                  Columns
                </DropdownToggle>
                <DropdownMenu
                  end
                  container="body"
                  className={workflowsStyles.columnsDropdownMenu}
                >
                  <div
                    className={workflowsStyles.columnsDropdownBody}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ColumnsSelector
                      columns={toolbarApi.columns}
                      userCols={toolbarApi.userCols}
                      tableName={toolbarApi.tableName}
                      hiddenByDefaultIds={toolbarApi.hiddenByDefaultIds}
                      alwaysHiddenIds={toolbarApi.alwaysHiddenIds}
                      containerClassName={
                        workflowsStyles.columnsSelectorEmbedded
                      }
                    />
                  </div>
                </DropdownMenu>
              </UncontrolledDropdown>
            </>
          )}
        </div>
        <div className={workflowsStyles.searchControls}>
          <div
            className={workflowsStyles.searchScopeToggle}
            role="group"
            aria-label="Search scope"
          >
            <button
              type="button"
              className={classnames(workflowsStyles.searchScopeBtn, {
                [workflowsStyles.searchScopeBtnActive]: searchScope === 'tab',
              })}
              onClick={() => setSearchScope('tab')}
            >
              This tab
            </button>
            <button
              type="button"
              className={classnames(workflowsStyles.searchScopeBtn, {
                [workflowsStyles.searchScopeBtnActive]: searchScope === 'all',
              })}
              onClick={() => setSearchScope('all')}
            >
              All statuses
            </button>
          </div>
          <div className={workflowsStyles.searchWrap}>
            <span className={workflowsStyles.searchIcon}>
              <SearchIcon />
            </span>
            <input
              className={workflowsStyles.searchInput}
              value={globalSearchQuery}
              onChange={(e) => setGlobalSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          </div>
        </div>
      </div>
      {isAllStatusesSearch && (
        <p className={workflowsStyles.searchScopeHint}>
          Showing matches across Active, Paused, Errored, and Archived.
        </p>
      )}

      {/* Each tab mounts its own Workflows instance with mutually exclusive filters */}
      <div hidden={activeTab !== TAB_ACTIVE} className={pagesStyles.fullHeight}>
        <Workflows
          archived={false}
          isActive={activeTab === TAB_ACTIVE}
          onToolbarChange={setToolbarApi}
          globalSearchQuery={activeTab === TAB_ACTIVE ? globalSearchQuery : ''}
          searchScope={activeTab === TAB_ACTIVE ? searchScope : 'tab'}
        />
      </div>
      <div
        hidden={activeTab !== TAB_PAUSED}
        className={pagesStyles.fullHeight}
      >
        <Workflows
          paused={true}
          archived={false}
          isActive={activeTab === TAB_PAUSED}
          onToolbarChange={setToolbarApi}
          globalSearchQuery={activeTab === TAB_PAUSED ? globalSearchQuery : ''}
          searchScope={activeTab === TAB_PAUSED ? searchScope : 'tab'}
        />
      </div>
      <div
        hidden={activeTab !== TAB_ERRORED}
        className={pagesStyles.fullHeight}
      >
        <Workflows
          errored={true}
          archived={false}
          isActive={activeTab === TAB_ERRORED}
          onToolbarChange={setToolbarApi}
          globalSearchQuery={activeTab === TAB_ERRORED ? globalSearchQuery : ''}
          searchScope={activeTab === TAB_ERRORED ? searchScope : 'tab'}
        />
      </div>
      <div
        hidden={activeTab !== TAB_ARCHIVED}
        className={pagesStyles.fullHeight}
      >
        <Workflows
          archived={true}
          isActive={activeTab === TAB_ARCHIVED}
          onToolbarChange={setToolbarApi}
          globalSearchQuery={activeTab === TAB_ARCHIVED ? globalSearchQuery : ''}
          searchScope={activeTab === TAB_ARCHIVED ? searchScope : 'tab'}
        />
      </div>

      <Can I="manage" a="workflows">
        {isCreateModalOpen && user && (
          <Row className={pagesStyles.fullHeight}>
            <Col sm={{ size: 12 }} className={pagesStyles.fullHeight}>
              <WorkflowModal
                workflow={newWorkflow}
                setWorkflow={setNewWorkflow}
                isOpen={isCreateModalOpen}
                toggle={setIsCreateModalOpen}
                user={user}
                isWorkflowsAdmin={isWorkflowsAdmin}
              />
            </Col>
          </Row>
        )}
      </Can>
    </div>
  );
};
